// Fase 5 · Reglas puras del monitor (reintentos y caducidad de la sesión de
// depuración). Sin DOM ni Redux para poder testearlas.

export const BACKOFF_START_MS = 10 * 1000;
export const BACKOFF_MAX_MS = 2 * 60 * 1000;
export const EXPIRES_KEY = "APIMONITOR_EXPIRES_AT";
/** Quién encendió la grabación: "manual" (Ajustes / /monitor) o "remote" (Live debug). */
export const ORIGIN_KEY = "APIMONITOR_ORIGIN";
/**
 * 2026-10-08 · Toda activación caduca, también la manual. Antes la manual no
 * tenía fecha y el dispositivo grababa indefinidamente (clicks en Live debug
 * sin sesión de depuración, lotes continuos y 429).
 */
export const MANUAL_TTL_MS = 4 * 60 * 60 * 1000;

/** Caducidad ISO de una activación manual hecha en `nowMs`. */
export const manualExpiresAt = (nowMs) => new Date(nowMs + MANUAL_TTL_MS).toISOString();
export const MAX_BATCH_ITEMS = 200; // tope del backend por batch

/** Siguiente espera tras un 429: 10 s, 20 s, 40 s… hasta 2 min. */
export function nextBackoffMs(prevMs) {
  if (!prevMs) return BACKOFF_START_MS;
  return Math.min(BACKOFF_MAX_MS, prevMs * 2);
}

/**
 * Estado del TTL de la sesión de monitorización guardado en localStorage.
 * Sin fecha → no caduca (solo grabaciones heredadas de antes de 2026-10-08;
 * la sincronización con el servidor las apaga si no hay sesión).
 */
export function ttlState(nowMs, expiresAtIso) {
  if (!expiresAtIso) return { hasTtl: false, expired: false, remainingMs: null };
  const t = new Date(expiresAtIso).getTime();
  if (!t || Number.isNaN(t)) return { hasTtl: false, expired: false, remainingMs: null };
  const remainingMs = t - nowMs;
  return { hasTtl: true, expired: remainingMs <= 0, remainingMs: Math.max(0, remainingMs) };
}

/** TTL de un toggle remoto: entre 1 y 240 min (30 por defecto). */
export function ttlMinutesFrom(payload) {
  return Math.max(1, Math.min(240, Number(payload?.ttlMinutes) || 30));
}

/**
 * Parte un conjunto de items ya ordenados por tiempo en lo que cabe en un
 * batch: primero las peticiones HTTP (cursor por tiempo) y luego los eventos
 * del recorder (cola). Devuelve cuántos de cada tipo entran.
 */
export function splitBatch(httpItems, recorderItems, max = MAX_BATCH_ITEMS) {
  const http = httpItems.slice(0, max);
  const rest = Math.max(0, max - http.length);
  const rec = recorderItems.slice(0, rest);
  return { http, rec };
}

/**
 * Estado del servidor (`GET api-monitor/debug-session`) → qué hacer en este
 * dispositivo. El aviso en tiempo real (`monitorToggle`) solo llega a quien
 * estaba conectado al activarla: al arrancar/volver se pregunta al servidor.
 *  - "start": hay sesión y aquí no se graba (o con otra caducidad).
 *  - "stop": no hay sesión y aquí se graba por una sesión remota, o sin
 *    caducidad (flag heredado o manual antiguo que antes no se apagaba nunca).
 *  - null: nada que cambiar (incluye la activación manual vigente: caduca sola).
 */
export function debugSessionSyncAction(server, { isMonitoring, expiresAtIso, origin = null }) {
  if (!server || typeof server.active !== "boolean") return null;
  if (server.active) {
    if (!isMonitoring) return "start";
    if (origin === "manual") return null; // la manual sigue con su propia caducidad
    if (server.expiresAt && server.expiresAt !== expiresAtIso) return "start";
    return null;
  }
  if (!isMonitoring) return null;
  const manualWithTtl = origin === "manual" && !!expiresAtIso;
  return manualWithTtl ? null : "stop";
}
